import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { requestPasscode, loginWithPasscode } = useAuth();
  const navigate = useNavigate();

  // 'email' = first step (enter email), 'passcode' = second step (enter code)
  const [step, setStep]   = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode]   = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy]   = useState(false);

  // ── Step 1: submit email ──────────────────────────────────────────────────

  const handleEmailSubmit = async e => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await requestPasscode(email);
      setStep('passcode');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // ── Step 2: verify passcode ───────────────────────────────────────────────

  const handlePasscodeSubmit = async e => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await loginWithPasscode(email, code);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // ── Cancel: reset to initial state ───────────────────────────────────────

  const handleCancel = () => {
    setStep('email');
    setEmail('');
    setCode('');
    setError('');
    setBusy(false);
  };

  return (
    <div className="row justify-content-center">
      <div className="col-md-5">
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <h4 className="card-title mb-4">Log In</h4>

            {error && <div className="alert alert-danger">{error}</div>}

            <form onSubmit={step === 'email' ? handleEmailSubmit : handlePasscodeSubmit}>

              {/* Email field — always visible, disabled after submission */}
              <div className="mb-3">
                <label className="form-label">Email address</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={step === 'passcode'}
                  required
                  autoFocus={step === 'email'}
                />
              </div>

              {/* Passcode field — hidden until step 2 */}
              {step === 'passcode' && (
                <div className="mb-3">
                  <label className="form-label">Pass code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    maxLength={6}
                  />
                  <div className="form-text mt-1">
                    Please check your email and enter the six character Pass code that was sent.
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-grow-1"
                  disabled={busy}
                >
                  {busy
                    ? (step === 'email' ? 'Sending…' : 'Logging in…')
                    : (step === 'email' ? 'Submit' : 'Log In')}
                </button>

                {step === 'passcode' && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCancel}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            <p className="mt-3 text-center small">
              Don't have an account? <Link to="/register">Register</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
